// Affaires d'un partenaire invité (spec § 3, décision de Maxime du 23/10/2026) : pas de pipeline ni de tableau, la
// liste des affaires transmises, leur étape en lecture et leur dernière note, avec un bandeau qui renvoie aux offres.
// La base refuse de toute façon à un invité de changer une affaire (migration 20261023030000).
import { useGetList, useLocaleState, useTranslate } from "ra-core";
import { Link } from "react-router";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

import { findDealLabel } from "../deals/dealUtils";
import { useConfigurationContext } from "../root/ConfigurationContext";
import type { Deal, DealNote } from "../types";
import { lastNoteByDeal } from "./invitedDealNotes";
import { OfferPage, useOfferOpen } from "./OfferPage";

/** Bandeau permanent : ce que l'abonnement ajoute au suivi des affaires transmises. */
export const InvitedBanner = () => {
  const translate = useTranslate();
  const offerOpen = useOfferOpen();
  return (
    <Card className="border-primary/40 bg-primary/5 py-4">
      <CardContent className="flex flex-col gap-3 px-4 sm:flex-row sm:items-center">
        <p className="flex-1 text-sm">
          {translate("resources.deals.invited.banner")}
        </p>
        {offerOpen && (
          <Button asChild size="sm" className="self-start sm:self-auto">
            <Link to={OfferPage.path}>
              {translate("resources.deals.invited.offers")}
            </Link>
          </Button>
        )}
      </CardContent>
    </Card>
  );
};

export const InvitedDeals = () => {
  const translate = useTranslate();
  const [locale = "fr"] = useLocaleState();
  const { dealStages } = useConfigurationContext();
  const { data: deals, isPending } = useGetList<Deal>("deals", {
    filter: { "archived_at@is": null },
    pagination: { page: 1, perPage: 500 },
    sort: { field: "updated_at", order: "DESC" },
  });
  const ids = (deals ?? []).map((deal) => deal.id);
  const { data: notes } = useGetList<DealNote>(
    "deal_notes",
    {
      filter: { "deal_id@in": `(${ids.join(",")})` },
      pagination: { page: 1, perPage: 1000 },
      sort: { field: "date", order: "DESC" },
    },
    { enabled: ids.length > 0 },
  );
  const lastNotes = lastNoteByDeal(notes ?? []);

  return (
    <div className="flex flex-col gap-4 pb-16 max-w-4xl">
      <InvitedBanner />
      <p className="text-sm text-muted-foreground">
        {translate("resources.deals.invited.intro")}
      </p>
      {isPending ? null : !deals?.length ? (
        <p className="text-sm text-muted-foreground py-8 text-center">
          {translate("resources.deals.invited.empty")}
        </p>
      ) : (
        deals.map((deal) => {
          const note = lastNotes.get(String(deal.id));
          return (
            <Card key={deal.id} className="py-3">
              <CardContent className="flex flex-col gap-2 px-4">
                <div className="flex flex-wrap items-start gap-2">
                  <span className="flex-1 min-w-0 font-medium leading-snug">
                    {deal.name}
                  </span>
                  <Badge
                    variant="secondary"
                    title={translate("resources.deals.invited.stage")}
                  >
                    {findDealLabel(dealStages, deal.stage) ?? deal.stage}
                  </Badge>
                </div>
                <p className="text-sm text-muted-foreground line-clamp-2">
                  {note ? (
                    <>
                      <span className="text-xs">
                        {translate("resources.deals.invited.last_note")} ·{" "}
                        {note.date
                          ? new Date(note.date).toLocaleDateString(locale)
                          : ""}{" "}
                        —{" "}
                      </span>
                      {note.text}
                    </>
                  ) : (
                    translate("resources.deals.invited.no_note")
                  )}
                </p>
                <div className="flex flex-wrap gap-2">
                  <Button asChild size="sm" variant="outline">
                    <Link to={`/deals/${deal.id}/show`}>
                      {translate("resources.deals.invited.add_note")}
                    </Link>
                  </Button>
                  {deal.project_id && (
                    <Button asChild size="sm" variant="ghost">
                      <Link to={`/projects/${deal.project_id}/show`}>
                        {translate("resources.deals.invited.open_project")}
                      </Link>
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })
      )}
    </div>
  );
};
